import React, { useState } from 'react';
import { X, DollarSign, PieChart, TrendingUp, Calendar, Building2, CheckCircle, ChevronDown, ChevronUp, Download } from 'lucide-react';

const BudgetDetailsModal = ({ budgetData, onClose }) => {
  const [expandedCategories, setExpandedCategories] = useState(new Set());

  if (!budgetData) return null;

  // Build category lookup by ID and name
  const categoryLookup = {};
  (budgetData.categories || []).forEach(cat => {
    if (cat.category_id) categoryLookup[cat.category_id] = cat;
    if (cat.category_name) categoryLookup[cat.category_name] = cat;
  });

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // Calculate total from suggestions if total_amount is not set
  const totalBudget = budgetData.total_amount ||
    (budgetData.suggestions || []).reduce((sum, s) => sum + (s.suggested_budget || 0), 0);

  // Get currency from budget data (defaults to USD)
  const currency = budgetData.base_currency || budgetData.currency || 'USD';

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getConfidenceBadge = (confidence) => {
    const colors = {
      high: 'bg-green-100 text-green-800',
      medium: 'bg-yellow-100 text-yellow-800',
      low: 'bg-red-100 text-red-800'
    };
    return colors[confidence] || colors.medium;
  };

  // Export budget to CSV
  const exportToCSV = () => {
    const rows = [];

    // Format number with commas
    const formatNumber = (num) => {
      if (!num && num !== 0) return '';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(num);
    };

    // Header row
    rows.push([
      'Category',
      `Budget Amount (${currency})`,
      'Growth Rate (%)',
      'Confidence',
      'Account Numbers',
      'Account Names',
      'Reasoning'
    ]);

    // Data rows from suggestions
    if (budgetData.suggestions && budgetData.suggestions.length > 0) {
      budgetData.suggestions.forEach(suggestion => {
        const category = categoryLookup[suggestion.category_id] || categoryLookup[suggestion.category_name];
        const accountDetails = category?.account_details || [];

        // Handle growth_rate format
        const growthRate = suggestion.growth_rate !== undefined
          ? (Math.abs(suggestion.growth_rate) < 1 ? suggestion.growth_rate * 100 : suggestion.growth_rate)
          : '';

        rows.push([
          suggestion.category_name || '',
          formatNumber(suggestion.suggested_budget),
          growthRate !== '' ? growthRate.toFixed(1) : '',
          suggestion.confidence || '',
          accountDetails.map(a => a.number).join('; '),
          accountDetails.map(a => a.name).join('; '),
          (suggestion.reasoning || '').replace(/"/g, '""') // Escape quotes
        ]);
      });
    } else if (budgetData.categories && budgetData.categories.length > 0) {
      // Fallback to categories if no suggestions
      budgetData.categories.forEach(cat => {
        rows.push([
          cat.category_name || cat.name || '',
          '',
          '',
          '',
          '',
          '',
          ''
        ]);
      });
    }

    // Add summary row
    rows.push([]);
    rows.push(['Total Budget', formatNumber(totalBudget), '', '', '', '', '']);
    rows.push(['Fiscal Year', budgetData.fiscal_year, '', '', '', '', '']);
    rows.push(['Currency', currency, '', '', '', '', '']);

    // Convert to CSV string
    const csvContent = rows.map(row =>
      row.map(cell => {
        const cellStr = String(cell);
        // Wrap in quotes if contains comma, newline, or quotes
        if (cellStr.includes(',') || cellStr.includes('\n') || cellStr.includes('"')) {
          return `"${cellStr}"`;
        }
        return cellStr;
      }).join(',')
    ).join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `Budget_${budgetData.budget_name || 'Export'}_FY${budgetData.fiscal_year || ''}_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[1000] backdrop-blur-sm">
      <div className="bg-white dark:bg-gray-800 rounded-xl w-[98%] max-w-[1800px] max-h-[90vh] flex flex-col shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-4 flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold text-white">{budgetData.budget_name || 'Budget Details'}</h2>
            <p className="text-indigo-200 text-sm">
              FY {budgetData.fiscal_year} • {budgetData.subsidiary_count > 1
                ? `${budgetData.subsidiary_count} Subsidiaries`
                : (budgetData.subsidiary || 'All Subsidiaries')}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Summary Cards */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-1">
                <DollarSign className="w-4 h-4" />
                <span className="text-sm font-medium">Total Budget</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {formatCurrency(totalBudget)}
              </p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-purple-600 dark:text-purple-400 mb-1">
                <PieChart className="w-4 h-4" />
                <span className="text-sm font-medium">Categories</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {budgetData.category_count || budgetData.categories?.length || 0}
              </p>
            </div>
            <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-1">
                <Calendar className="w-4 h-4" />
                <span className="text-sm font-medium">Fiscal Year</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {budgetData.fiscal_year}
              </p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
              <div className="flex items-center gap-2 text-orange-600 dark:text-orange-400 mb-1">
                <Building2 className="w-4 h-4" />
                <span className="text-sm font-medium">Status</span>
              </div>
              <p className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-500" />
                {budgetData.status === 'completed' ? 'Saved' : budgetData.status}
              </p>
            </div>
          </div>
        </div>

        {/* Budget Categories & Suggestions */}
        <div className="flex-1 overflow-y-auto p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-indigo-600" />
            Budget Allocations
          </h3>

          {budgetData.suggestions && budgetData.suggestions.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {budgetData.suggestions.map((suggestion, idx) => {
                // Handle growth_rate - could be decimal (0.05) or percentage (5)
                const growthRate = suggestion.growth_rate !== undefined
                  ? (Math.abs(suggestion.growth_rate) < 1 ? suggestion.growth_rate * 100 : suggestion.growth_rate)
                  : null;

                // Find matching category for account details
                const category = categoryLookup[suggestion.category_id] || categoryLookup[suggestion.category_name];
                const accountDetails = category?.account_details || [];
                const isExpanded = expandedCategories.has(suggestion.category_id || idx);

                return (
                  <div
                    key={idx}
                    className="bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden"
                  >
                    {/* Header - Clickable to expand */}
                    <div
                      onClick={() => toggleCategory(suggestion.category_id || idx)}
                      className="p-4 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex justify-between items-start mb-2">
                        <h4 className="font-semibold text-gray-900 dark:text-white text-base">
                          {suggestion.category_name || `Category ${idx + 1}`}
                        </h4>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getConfidenceBadge(suggestion.confidence)}`}>
                            {suggestion.confidence}
                          </span>
                          {accountDetails.length > 0 && (
                            isExpanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />
                          )}
                        </div>
                      </div>

                      {/* Budget Amount */}
                      <div className="mb-2">
                        <p className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
                          {formatCurrency(suggestion.suggested_budget)}
                        </p>
                        <div className="flex items-center gap-3">
                          {growthRate !== null && (
                            <span className={`text-sm font-medium ${growthRate >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                              {growthRate >= 0 ? '↑' : '↓'} {Math.abs(growthRate).toFixed(1)}% YoY
                            </span>
                          )}
                          {accountDetails.length > 0 && (
                            <span className="text-xs text-gray-500">
                              {accountDetails.length} accounts
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Reasoning */}
                      {suggestion.reasoning && (
                        <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                          {suggestion.reasoning}
                        </p>
                      )}
                    </div>

                    {/* Expanded Account Details */}
                    {isExpanded && accountDetails.length > 0 && (
                      <div className="border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 p-3 max-h-48 overflow-y-auto">
                        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2">Accounts in this category:</p>
                        <div className="space-y-1">
                          {accountDetails.map((acc, accIdx) => (
                            <div key={accIdx} className="text-xs text-gray-600 dark:text-gray-300 flex gap-2">
                              <span className="font-mono text-gray-400">{acc.number}</span>
                              <span className="truncate">{acc.name}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : budgetData.categories && budgetData.categories.length > 0 ? (
            <div className="space-y-2">
              {budgetData.categories.map((cat, idx) => (
                <div
                  key={idx}
                  className="flex justify-between items-center bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3"
                >
                  <span className="text-gray-900 dark:text-white">
                    {cat.category_name || cat.name || `Category ${idx + 1}`}
                  </span>
                  <span className="font-medium text-indigo-600">
                    {cat.account_count || 0} accounts
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No budget data available</p>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 px-6 py-4 flex justify-end gap-3">
          <button
            onClick={exportToCSV}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Export CSV
          </button>
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BudgetDetailsModal;
